import React, { Component } from 'react';
import { Provider } from 'react-redux';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import { getLocalStorage, removeLocalStorage } from './utils/web-storage';

import 'react-toastify/dist/ReactToastify.css';
import './App.css';

import { axiosInterceptor } from './inits/axios';
import store from './store';
import logs from './components/Common/HistoryLogs';

import { getCurrentUser } from './store/actions/auth/auth.action';

import { PrivateRoute, PublicRoute } from './utils/route';

// screens
import LoginContainer from './containers/Login/LoginContainer';
import DifficultiesContainer from './containers/Difficulties/DifficultiesContainer';
import CreateDifficultyContainer from './containers/Difficulties/CreateDifficultyContainer';
import ComposersContainer from './containers/Composers/ComposersContainer';
import CreateComposerContainer from './containers/Composers/CreateComposerContainer';
import ErasContainer from './containers/Eras/ErasContainer';
import CreateEraContainer from './containers/Eras/CreateEraContainer';
import InstrumentsContainer from './containers/Instruments/InstrumentsContainer';
import CreateInstrumentContainer from './containers/Instruments/CreateInstrumentContainer';
import PiecesContainer from './containers/Pieces/PiecesContainer';
import CreatePieceContainer from './containers/Pieces/CreatePieceContainer';

import PiecesVideoContainer from './containers/PiecesVideo/PiecesVideoContainer';
import CreatePieceVideoContainer from './containers/PiecesVideo/CreatePieceVideoContainer';
import PiecesInstrumentContainer from './containers/PiecesInstrument/PiecesInstrumentContainer';
import CreatePieceInstrumentContainer from './containers/PiecesInstrument/CreatePieceInstrumentContainer';

import ArtistListContainer from './containers/Artist/ArtistListContainer';
import CreateArtistContainer from './containers/Artist/CreateArtistContainer';

import MemberListContainer from './containers/Member/MemberListContainer';
import CreateMemberContainer from './containers/Member/CreateMemberContainer';
import CreateFaqsContainer from './containers/Faqs/CreateFaqsContainer';
import FaqsContainer from './containers/Faqs/FaqsContainer';

import SubscriptionListContainer from './containers/Subscriptions/subscriptionsContainer';
import CreateSubscriptionContainer from './containers/Subscriptions/CreateSubscriptionContainer';
import CreateCouponsContainer from './containers/Coupons/CreateCouponsContainer';
import CouponsContainer from './containers/Coupons/CouponsContainer';

import OrdersContainer from './containers/Orders/OrdersContainer';

import AdminsContainer from './containers/Admins/AdminsContainer';
import CreateAdminsContainer from './containers/Admins/CreateAdminsContainer';
import NewCategoryContainer from './containers/New Category/NewCategoryContainer';

import UsersListContainer from './containers/Users/UsersListContainer';
import SingleUserContainer from './containers/Users/SingleUserContainer';
import CreateUserContainer from './containers/Users/CreateUserContainer';
import DataAnalyticsContainer from './containers/Analytics/DataAnalyticsContainer';

// component
import PageLoader from './components/Common/PageLoader';
import Header from './containers/Common/Header';
import Sidebar from './containers/Common/Sidebar';

import FeaturesContainer from './containers/Features/FeaturesContainer';
import CreateFeatureContainer from './containers/Features/CreateFeatureContainer';

axiosInterceptor(store);

// Component to handle location changes
function LocationHandler({ onLocationChange }) {
  const location = useLocation();

  React.useEffect(() => {
    onLocationChange(location.pathname);
  }, [location.pathname, onLocationChange]);

  return null;
}

class App extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      sidebarOpen: true,
      loadSidebar: false,
    };
  }

  componentDidMount() {
    this.authenticateUser();
  }

  handleLocationChange = (pathname) => {
    const fixedElements = document.querySelector('.fixedElements');

    if (fixedElements) {
      if (pathname === '/') {
        fixedElements.classList.add('loginScreen');
        this.setState({ loadSidebar: false });
      } else {
        if (!this.state.loadSidebar) {
          this.setState({ loadSidebar: true });
        }
        fixedElements.classList.remove('loginScreen');
      }
    }
  };

  authenticateUser = async () => {
    try {
      await logs.authenticateGUser();
      const accessToken = getLocalStorage('accessToken');

      if (accessToken) {
        try {
          await store.dispatch(getCurrentUser(accessToken));
        } catch (error) {
          console.error('Error authenticating user:', error);
          // If token is invalid, remove it
          removeLocalStorage('accessToken');
        }
      }
    } catch (error) {
      console.error('Error in authentication process:', error);
    } finally {
      this.setState({ loading: false });
    }
  };

  toggleSidebar = () => {
    this.setState((prev) => ({
      ...prev,
      sidebarOpen: !prev.sidebarOpen,
    }));
  };

  render() {
    const { sidebarOpen, loadSidebar, loading } = this.state;

    if (loading) {
      return <PageLoader />;
    }

    // Get user from store dynamically
    const user = store.getState().auth?.user;

    return (
      <Provider store={store}>
        <Router>
          <LocationHandler onLocationChange={this.handleLocationChange} />
          <div className="top-container">
            <ToastContainer autoClose={3000} />
            {loadSidebar && <Sidebar sidebarOpen={sidebarOpen} />}

            <div className="fixedElements">
              {user && Object.keys(user).length ? (
                <Header
                  sidebarOpen={sidebarOpen}
                  toggleSidebar={this.toggleSidebar}
                />
              ) : null}

              <div id="contentWrapper" className="content-wrapper">
                <Routes>
                  {/* Public Routes */}
                  <Route
                    path="/"
                    element={
                      <PublicRoute>
                        <LoginContainer />
                      </PublicRoute>
                    }
                  />

                  {/* Private Routes */}
                  <Route
                    path="/analytics"
                    element={
                      <PrivateRoute>
                        <DataAnalyticsContainer />
                      </PrivateRoute>
                    }
                  />

                  <Route
                    path="/difficulties"
                    element={
                      <PrivateRoute>
                        <DifficultiesContainer />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/difficulty/create"
                    element={
                      <PrivateRoute>
                        <CreateDifficultyContainer />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/difficulty/:mode/:id"
                    element={
                      <PrivateRoute>
                        <CreateDifficultyContainer />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/composers"
                    element={
                      <PrivateRoute>
                        <ComposersContainer />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/composer/create"
                    element={
                      <PrivateRoute>
                        <CreateComposerContainer />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/composer/:mode/:id"
                    element={
                      <PrivateRoute>
                        <CreateComposerContainer />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/eras"
                    element={
                      <PrivateRoute>
                        <ErasContainer />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/era/create"
                    element={
                      <PrivateRoute>
                        <CreateEraContainer />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/era/:mode/:id"
                    element={
                      <PrivateRoute>
                        <CreateEraContainer />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/instruments"
                    element={
                      <PrivateRoute>
                        <InstrumentsContainer />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/instrument/create"
                    element={
                      <PrivateRoute>
                        <CreateInstrumentContainer />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/instrument/:mode/:id"
                    element={
                      <PrivateRoute>
                        <CreateInstrumentContainer />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/pieces"
                    element={
                      <PrivateRoute>
                        <PiecesContainer />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/piece/create"
                    element={
                      <PrivateRoute>
                        <CreatePieceContainer />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/piece/:mode/:id"
                    element={
                      <PrivateRoute>
                        <CreatePieceContainer />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/pieces-video"
                    element={
                      <PrivateRoute>
                        <PiecesVideoContainer />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/piece-video/create"
                    element={
                      <PrivateRoute>
                        <CreatePieceVideoContainer />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/pieces-instrument"
                    element={
                      <PrivateRoute>
                        <PiecesInstrumentContainer />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/piece-instrument/create"
                    element={
                      <PrivateRoute>
                        <CreatePieceInstrumentContainer />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/artists"
                    element={
                      <PrivateRoute>
                        <ArtistListContainer />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/artist/create"
                    element={
                      <PrivateRoute>
                        <CreateArtistContainer />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/artist/:mode/:id"
                    element={
                      <PrivateRoute>
                        <CreateArtistContainer />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/members"
                    element={
                      <PrivateRoute>
                        <MemberListContainer />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/member/create"
                    element={
                      <PrivateRoute>
                        <CreateMemberContainer />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/member/:mode/:id"
                    element={
                      <PrivateRoute>
                        <CreateMemberContainer />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/faqs"
                    element={
                      <PrivateRoute>
                        <FaqsContainer />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/faq/create"
                    element={
                      <PrivateRoute>
                        <CreateFaqsContainer />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/faq/:mode/:id"
                    element={
                      <PrivateRoute>
                        <CreateFaqsContainer />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/subscriptions"
                    element={
                      <PrivateRoute>
                        <SubscriptionListContainer />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/subscription/:mode/:id"
                    element={
                      <PrivateRoute>
                        <CreateSubscriptionContainer />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/subscription/create"
                    element={
                      <PrivateRoute>
                        <CreateSubscriptionContainer />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/features"
                    element={
                      <PrivateRoute>
                        <FeaturesContainer />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/feature/create"
                    element={
                      <PrivateRoute>
                        <CreateFeatureContainer />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/feature/:mode/:id"
                    element={
                      <PrivateRoute>
                        <CreateFeatureContainer />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/coupons"
                    element={
                      <PrivateRoute>
                        <CouponsContainer />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/coupon/create"
                    element={
                      <PrivateRoute>
                        <CreateCouponsContainer />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/coupon/:mode/:id"
                    element={
                      <PrivateRoute>
                        <CreateCouponsContainer />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/orders"
                    element={
                      <PrivateRoute>
                        <OrdersContainer />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/admins"
                    element={
                      <PrivateRoute>
                        <AdminsContainer />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/admins/create"
                    element={
                      <PrivateRoute>
                        <CreateAdminsContainer />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/admins/edit/:id"
                    element={
                      <PrivateRoute>
                        <CreateAdminsContainer />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/users"
                    element={
                      <PrivateRoute>
                        <UsersListContainer />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/users/:id"
                    element={
                      <PrivateRoute>
                        <SingleUserContainer />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/user/create"
                    element={
                      <PrivateRoute>
                        <CreateUserContainer />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/new-category"
                    element={
                      <PrivateRoute>
                        <NewCategoryContainer />
                      </PrivateRoute>
                    }
                  />

                  {/* 404 Route */}
                  <Route path="*" element={<div>Page Not Found</div>} />
                </Routes>
              </div>
            </div>
          </div>
        </Router>
      </Provider>
    );
  }
}

export default App;
